import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, MessageCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUserId(JSON.parse(userData).id);
    }
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedPost(conv);
    fetchMessages(conv.post_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPost) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/messages`,
        { post_id: selectedPost.post_id, message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages(selectedPost.post_id);
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="glassmorphism border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/home')}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-orange-600" />
              <h1 className="text-2xl font-bold text-stone-900">Messages</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 200px)' }}>
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[calc(100vh-300px)]">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                    <p className="text-sm">No conversations yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSelectConversation(conv)}
                        className={`p-4 rounded-xl cursor-pointer transition-all ${
                          selectedPost?.post_id === conv.post_id
                            ? 'bg-orange-50 border-2 border-orange-200'
                            : 'bg-stone-50 hover:bg-stone-100 border-2 border-transparent'
                        }`}
                      >
                        <h4 className="font-semibold text-stone-900 mb-1">{conv.post_title}</h4>
                        <p className="text-sm text-stone-600 line-clamp-1">{conv.last_message}</p>
                        <p className="text-xs text-stone-500 mt-1">{conv.other_user_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            {selectedPost ? (
              <>
                <CardHeader>
                  <CardTitle>{selectedPost.post_title}</CardTitle>
                  <p className="text-sm text-stone-600">Chat with {selectedPost.other_user_name}</p>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[calc(100vh-400px)] mb-4 pr-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-stone-500">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                        <p className="text-sm">No messages yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg, idx) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                msg.sender_id === currentUserId
                                  ? 'bg-orange-600 text-white'
                                  : 'bg-stone-100 text-stone-900'
                              }`}
                            >
                              <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                              <p className="text-sm">{msg.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={sending || !newMessage.trim()}
                      className="rounded-full btn-primary"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full">
                <div className="text-center text-stone-500">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                  <p>Select a conversation to start messaging</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}